"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type Country = {
  code: string;
  name: string;
  dial: string;
  flag: string;
  example: string;
};

// Diaspora (origines) + Afrique (destinations) + pays courants
// `example` = numéro d'exemple en format national (sert de placeholder)
export const COUNTRIES: Country[] = [
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦", example: "514 555 0199" },
  { code: "US", name: "États-Unis", dial: "+1", flag: "🇺🇸", example: "415 555 0199" },
  { code: "FR", name: "France", dial: "+33", flag: "🇫🇷", example: "06 12 34 56 78" },
  { code: "BE", name: "Belgique", dial: "+32", flag: "🇧🇪", example: "0470 12 34 56" },
  { code: "CH", name: "Suisse", dial: "+41", flag: "🇨🇭", example: "079 123 45 67" },
  { code: "GB", name: "Royaume-Uni", dial: "+44", flag: "🇬🇧", example: "07700 900123" },
  { code: "DE", name: "Allemagne", dial: "+49", flag: "🇩🇪", example: "0151 23456789" },
  { code: "ES", name: "Espagne", dial: "+34", flag: "🇪🇸", example: "612 34 56 78" },
  { code: "IT", name: "Italie", dial: "+39", flag: "🇮🇹", example: "312 345 6789" },
  { code: "CN", name: "Chine", dial: "+86", flag: "🇨🇳", example: "131 2345 6789" },
  { code: "AE", name: "Émirats arabes unis", dial: "+971", flag: "🇦🇪", example: "050 123 4567" },
  { code: "GN", name: "Guinée", dial: "+224", flag: "🇬🇳", example: "620 00 00 00" },
  { code: "SN", name: "Sénégal", dial: "+221", flag: "🇸🇳", example: "70 123 45 67" },
  { code: "CI", name: "Côte d'Ivoire", dial: "+225", flag: "🇨🇮", example: "01 23 45 67 89" },
  { code: "ML", name: "Mali", dial: "+223", flag: "🇲🇱", example: "65 01 23 45" },
  { code: "CM", name: "Cameroun", dial: "+237", flag: "🇨🇲", example: "6 71 23 45 67" },
  { code: "EG", name: "Égypte", dial: "+20", flag: "🇪🇬", example: "0100 123 4567" },
  { code: "MA", name: "Maroc", dial: "+212", flag: "🇲🇦", example: "0612 345678" },
  { code: "DZ", name: "Algérie", dial: "+213", flag: "🇩🇿", example: "0551 23 45 67" },
  { code: "TN", name: "Tunisie", dial: "+216", flag: "🇹🇳", example: "20 123 456" },
  { code: "NG", name: "Nigeria", dial: "+234", flag: "🇳🇬", example: "0801 234 5678" },
  { code: "GH", name: "Ghana", dial: "+233", flag: "🇬🇭", example: "024 123 4567" },
];

export const DEFAULT_COUNTRY = COUNTRIES[0]; // Canada

export function CountrySelect({
  value,
  onChange,
}: {
  value: Country;
  onChange: (c: Country) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Fermer au clic extérieur / Échap
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-full items-center gap-1.5 rounded-l-xl border border-r-0 border-[#E0D5C6] bg-cream-soft px-3 text-[15px] text-espresso outline-none transition hover:bg-cream"
      >
        <span className="text-lg leading-none">{value.flag}</span>
        <span className="font-medium">{value.dial}</span>
        <ChevronDown
          size={15}
          className={cn(
            "text-ink-faint transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute left-0 top-[calc(100%+6px)] z-[210] max-h-[240px] w-[260px] overflow-y-auto rounded-xl border border-line-strong bg-white py-1.5 shadow-[0_24px_50px_-20px_rgba(40,20,8,.35)]"
        >
          {COUNTRIES.map((c) => {
            const active = c.code === value.code;
            return (
              <li key={c.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(c);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition-colors",
                    active
                      ? "bg-[#F4E5DD] text-terracotta-dark"
                      : "text-espresso hover:bg-cream-soft",
                  )}
                >
                  <span className="text-lg leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-ink-faint">{c.dial}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
